
import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

interface BusinessLogoProps {
  logoUrl?: string;
  avatarUrl?: string; // NEW: user's avatar URL (will be displayed as rounded)
  businessName: string;
  size?: "sm" | "md" | "lg" | "xl";
  borderRadius?: "none" | "sm" | "md" | "lg" | "xl" | "full";
  className?: string;
  // Legacy support
  isRounded?: boolean;
}

const sizeClasses = {
  sm: "h-8 w-8",
  md: "h-14 w-14", 
  lg: "h-16 w-16",
  xl: "h-20 w-20"
};

const textSizeClasses = {
  sm: "text-sm",
  md: "text-lg",
  lg: "text-xl", 
  xl: "text-2xl"
};

const borderRadiusClasses = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  full: "rounded-full"
};

export const BusinessLogo: React.FC<BusinessLogoProps> = ({
  logoUrl,
  avatarUrl,
  businessName,
  size = "md",
  borderRadius = "none",
  isRounded = false, // Legacy support
  className
}) => {
  // Handle legacy isRounded prop
  const actualBorderRadius = borderRadius === "none" && isRounded ? "full" : borderRadius;
  const isCircular = actualBorderRadius === "full";
  
  // Prioritize avatar URL if available (always show as rounded)
  const primaryImageUrl = avatarUrl || logoUrl;
  const isAvatar = !!avatarUrl;
  
  // If not circular and we have a logo URL (and no avatar), use regular img element with max-width
  if (!isCircular && primaryImageUrl && !isAvatar) {
    return (
      <div className={cn("flex", className)}>
        <img
          src={primaryImageUrl}
          alt={businessName}
          className={cn(
            "object-contain max-w-full",
            sizeClasses[size],
            borderRadiusClasses[actualBorderRadius]
          )}
        />
      </div>
    );
  }

  // Use Avatar for circular logos, avatars, or fallback
  return (
    <Avatar className={cn(sizeClasses[size], borderRadiusClasses[isAvatar ? "full" : actualBorderRadius], className)}>
      {primaryImageUrl && (
        <AvatarImage 
          src={primaryImageUrl} 
          alt={businessName}
          className={isAvatar ? "object-cover" : "object-contain"}
        />
      )}
      <AvatarFallback className={cn(textSizeClasses[size], "font-semibold rounded-full")}>
        {businessName.charAt(0).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
};
